import React, { useEffect, useMemo, useRef } from 'react';

import { getCurrentWeekRange, getFormattedDate } from 'utils/date';

import LFModal from 'components/common/LFModal';
import Info from 'components/common/inputWrapper/components/Info';

import { useFetchRosterData } from 'hooks/query/useFetchRosterData';
import { handleError } from 'utils/errorHandler';

import { EmployeeRosterTable } from './EmployeeRosterTable';

type Employee = {
  id?: number | string;
  firstName?: string;
};

type TriggerButtonProps = {
  onClick: () => void;
  interactive: boolean;
  isLoading?: boolean;
};

type Props = {
  TriggerBtn?: React.ComponentType<TriggerButtonProps>;
  isOpen?: boolean;
  onClose?: () => void;
  employee?: Employee;
};

type EmployeeRosterRow = {
  date?: string;
  workDate?: string;
  workShift?: string;
  availabilityTime?: {
    startTime: string;
    startTimeType: string;
    endTime: string;
    endTimeType: string;
  };
  location?: string;
};

export function EmployeeRosterModal(props: Props) {
  const { TriggerBtn, isOpen = false, onClose, employee } = props;

  const [weekStartDate, weekEndDate] = getCurrentWeekRange(getFormattedDate());

  const {
    isLoading: isRosterDataLoading,
    data: rosterData,
    isError: isRosterDataError,
    error: rosterDataError,
    errorUpdatedAt: rosterDataErrorUpdatedAt,
  } = useFetchRosterData({
    userIds: employee?.id,
    startDate: weekStartDate,
    endDate: weekEndDate,
  });
  const lastHandledRosterDataErrorAtRef = useRef(0);
  const parsedRosterData = Array.isArray(rosterData) ? (rosterData as EmployeeRosterRow[]) : [];

  const modalHeader = useMemo(() => `${employee?.firstName}'s Roster`, [employee?.firstName]);

  useEffect(() => {
    if (!isRosterDataError || !rosterDataError || rosterDataErrorUpdatedAt <= lastHandledRosterDataErrorAtRef.current) {
      return;
    }

    handleError(rosterDataError as object);
    lastHandledRosterDataErrorAtRef.current = rosterDataErrorUpdatedAt;
  }, [isRosterDataError, rosterDataError, rosterDataErrorUpdatedAt]);

  return (
    <LFModal
      TriggerBtn={TriggerBtn}
      isOpen={isOpen}
      onClose={onClose}
      closeButton
      htmlContent={
        <div className="roster-alert">
          <Info
            shouldAlert={false}
            message={`This is ${employee?.firstName}'s expected roster for the week, not actual attendance.`}
            className="roster-alert__wrapper"
          />

          <EmployeeRosterTable isLoading={isRosterDataLoading} rosterData={parsedRosterData} />
        </div>
      }
      title={{
        text: modalHeader,
        type: 'info',
      }}
      className="employee-roster-modal"
    />
  );
}
